import type { DocumentRef } from '../../lib/documentReader'
import { formatNumberString, formatOfficerDate, humanizeToken } from '../../lib/officerFormat'
import DocumentLink from '../documents/DocumentLink'
import type { EditScope } from './contract'

export interface DatedBalanceView {
  value: string | null
  financialDate: string | null
  denomination: string | null
  currency: string | null
}
export interface DateIntervalView {
  key: string
  from: string | null
  to: string | null
  status: 'first_reference' | 'balanced' | 'break' | 'unknown'
  expected: string | null
  stated: string
  difference: string | null
  findings: string[]
}
export interface InstrumentDateResultView {
  asOf: string
  selectedBefore: DatedBalanceView | null
  replacement: DatedBalanceView | null
  beforeCurrentReference: DatedBalanceView | null
  resultingCurrentReference: DatedBalanceView | null
  beforeAnchor: DatedBalanceView | null
  resultingAnchor: DatedBalanceView | null
  calculatedBefore: DatedBalanceView | null
  calculatedAfter: DatedBalanceView | null
  intervalsBefore: DateIntervalView[]
  intervalsAfter: DateIntervalView[]
  reasons: string[]
  documents: DocumentRef[]
}

function quantity(value: DatedBalanceView | null): string {
  if (value?.value == null) return 'Unavailable'
  const measure = value.denomination === 'units' ? 'units' : value.currency
  return [formatNumberString(value.value), measure].filter(Boolean).join(' ')
}
function Balance({ value }: { value: DatedBalanceView | null }) {
  return (
    <>
      <strong>{quantity(value)}</strong>
      <div className="record-edit-current">
        {formatOfficerDate(value?.financialDate) ?? 'Date not recorded'}
      </div>
    </>
  )
}
function Intervals({ label, rows }: { label: string; rows: DateIntervalView[] }) {
  const status = {
    first_reference: 'First recorded balance',
    balanced: 'Balanced',
    break: 'Difference remains',
    unknown: 'Comparison unavailable',
  }
  return (
    <div>
      <h4>{label}</h4>
      {rows.length ? (
        <div className="record-date-table">
          <table aria-label={`${label} balance intervals`}>
            <thead>
              <tr>
                <th>Interval</th>
                <th>Expected</th>
                <th>Stated</th>
                <th>Difference</th>
                <th>Comparison</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key}>
                  <th>
                    {row.from ? `${formatOfficerDate(row.from)} → ` : ''}
                    {formatOfficerDate(row.to) ?? 'Date not recorded'}
                  </th>
                  <td>{row.expected == null ? 'Unavailable' : formatNumberString(row.expected)}</td>
                  <td>{formatNumberString(row.stated)}</td>
                  <td>
                    {row.difference == null ? 'Unavailable' : formatNumberString(row.difference)}
                  </td>
                  <td>
                    {status[row.status]}
                    {row.findings.map((finding) => (
                      <div className="record-edit-current" key={finding}>
                        {humanizeToken(finding)}
                      </div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p>No dated interval comparison is available.</p>
      )}
    </div>
  )
}

/** Display only. Every balance and interval comes from the admitted server plan. */
export default function InstrumentDateResult({
  value,
  scope,
  instrumentId,
}: {
  value: InstrumentDateResultView
  scope: EditScope
  instrumentId: string
}) {
  return (
    <section className="record-date-result" aria-label="Balance date result">
      <h4>Balance date result</h4>
      <p>Calculation as of {formatOfficerDate(value.asOf)}</p>
      <div className="record-date-table">
        <table aria-label="Balance date comparison">
          <thead>
            <tr>
              <th>Balance</th>
              <th>Before correction</th>
              <th>After correction</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th>Selected source balance</th>
              <td>
                <Balance value={value.selectedBefore} />
              </td>
              <td>
                <Balance value={value.replacement} />
              </td>
            </tr>
            <tr>
              <th>Current recorded reference</th>
              <td>
                <Balance value={value.beforeCurrentReference} />
              </td>
              <td>
                <Balance value={value.resultingCurrentReference} />
              </td>
            </tr>
            <tr>
              <th>Balance used for calculation</th>
              <td>
                <Balance value={value.beforeAnchor} />
              </td>
              <td>
                <Balance value={value.resultingAnchor} />
              </td>
            </tr>
            <tr>
              <th>Calculated balance</th>
              <td>
                <Balance value={value.calculatedBefore} />
              </td>
              <td>
                <Balance value={value.calculatedAfter} />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      {value.reasons.length ? (
        <div role="status" className="record-edit-warning">
          <p>This result needs further review before it can be recorded.</p>
          <ul>
            {value.reasons.map((reason) => (
              <li key={reason}>{humanizeToken(reason)}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <details>
        <summary>Compare balance intervals</summary>
        <Intervals label="Before correction" rows={value.intervalsBefore} />
        <Intervals label="After correction" rows={value.intervalsAfter} />
      </details>
      {value.documents.length ? (
        <div className="record-edit-documents">
          <span>Sources used for this result</span>
          {value.documents.map((document) => (
            <DocumentLink
              key={document.document_id}
              document={document}
              context={{
                fundId: scope.fund_id ?? undefined,
                tab: 'record',
                focus: `instrument:${instrumentId}`,
              }}
            />
          ))}
        </div>
      ) : null}
    </section>
  )
}
